import { clsx, type ClassValue } from "clsx"
import dayjs from "dayjs"
import { isValidPhoneNumber as LibPhoneIsValidPhoneNumber } from "libphonenumber-js"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(
  date?: string | number | Date | null | dayjs.Dayjs
): string {
  return dayjs(date).format("MMM, DD, YYYY")
}

export const formatDateForAPI = (date?: Date) => {
  if (!date) return ""
  return dayjs(date).format("YYYY-MM-DD")
}

export const formatDateForAPIUTC = (date?: Date) => {
  if (!date) return ""
  // Convert to UTC and format as YYYY-MM-DD
  return dayjs(date).utc().format("YYYY-MM-DD")
}

export function formatDateTime(
  date?: string | number | Date | null | dayjs.Dayjs
): string {
  return dayjs(date).format("MMM DD YYYY hh:mm a")
}

export function getRelativeTime(
  date?: string | number | Date | null | dayjs.Dayjs
): string {
  if (!date) return ""
  const now = dayjs()
  const target = dayjs(date)
  const diffInDays = now.diff(target, "day")
  const diffInHours = now.diff(target, "hour")
  const diffInMinutes = now.diff(target, "minute")

  if (diffInMinutes < 1) return "Just now"
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInHours < 24) return `${diffInHours}h ago`
  if (diffInDays === 0) return "Today"
  if (diffInDays === 1) return "Yesterday"
  if (diffInDays < 7) return `${diffInDays}d ago`
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}mo ago`
  return `${Math.floor(diffInDays / 365)}y ago`
}

/**
 * Formats a date as a relative time string (Today, Yesterday, X days ago, etc.)
 * Falls back to formatted date for dates older than 30 days
 */
export function formatRelativeDate(
  dateString?: string | number | Date | null
): string {
  if (!dateString) return "—"
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export function utcToZonedTime(date?: string | number | Date | null) {
  return dayjs(date).utc(true)
}

export function zonedTimeToUtc(
  date?: string | number | Date | null | dayjs.Dayjs
) {
  const dateTime = dayjs(date).utc(false)
  return dateTime
}

export const isValidPhoneNumber = (input: string) => {
  return LibPhoneIsValidPhoneNumber(input)
}

export const formatAddress = (param?: {
  zipCode?: string | null
  address?: string | null
  state?: string | null
}) => {
  if (!param?.address) return "-"
  const { address, state, zipCode } = param
  return `${address}, ${state}, ${zipCode}`
}
